// src/pages/ExperimentsPage.jsx
// src/pages/ExperimentsPage.jsx
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, Alert, Button, ButtonGroup, Badge,
  Toast, ToastContainer, Modal, Form, InputGroup,
} from 'react-bootstrap';
import { getExperiments, deleteExperiments, searchExperiments } from '../api/photoProcessingApi';
import { groupsAPI } from '../api/groupsApi';
import ExperimentCard from '../components/Experiments/ExperimentCard';
import ExperimentRow from '../components/Experiments/ExperimentRow';
import './ExperimentsPage.css';

const TAB_ALL  = '__all__';
const TAB_MINE = '__mine__';

// ── Вид ленты (step5): переключатель grid / row, row — по умолчанию ────────
const VIEW_MODE_STORAGE_KEY = 'chrometrica.experiments.viewMode';

// page_size для одного запроса зависит от вида: строка компактнее карточки
// примерно в 3-4 раза, поэтому за один "экран подгрузки" в row-виде должно
// приходить кратно больше элементов, иначе IntersectionObserver будет
// триггерить подгрузку почти сразу после первого рендера.
const PAGE_SIZE_GRID = 20; // как и раньше — дефолт бэкенда (StandardSetPagination)
const PAGE_SIZE_ROW  = 60; // в 3 раза больше; max_page_size на бэкенде — 100
const pageSizeFor = (mode) => (mode === 'row' ? PAGE_SIZE_ROW : PAGE_SIZE_GRID);

const ExperimentsPage = () => {
  const navigate = useNavigate();

  // ── Pagination / fetch state ──────────────────────────────────────────────
  const [allExperiments, setAllExperiments] = useState([]);
  const [page,           setPage]           = useState(1);
  const [hasMore,        setHasMore]        = useState(true);
  const [loading,        setLoading]        = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error,          setError]          = useState(null);

  // ── View mode (grid / row) ──────────────────────────────────────────────
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_MODE_STORAGE_KEY) || 'row';
    } catch {
      return 'row';
    }
  });

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchQuery,        setSearchQuery]        = useState('');
  const [searchDateFrom,     setSearchDateFrom]     = useState('');
  const [searchDateTo,       setSearchDateTo]       = useState('');
  const [searchResults,      setSearchResults]      = useState([]);
  const [searchHasMore,      setSearchHasMore]      = useState(false);
  const [searchPage,         setSearchPage]         = useState(1);
  const [searchLoading,      setSearchLoading]      = useState(false);
  const [searchError,        setSearchError]        = useState(null);
  const [showSearchResults,  setShowSearchResults]  = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const loadingRef          = useRef(false);
  const hasMoreRef          = useRef(true);
  const pageRef             = useRef(1);
  const isMountedRef        = useRef(true);
  const observerRef         = useRef(null);
  const searchContainerRef  = useRef(null);
  const viewModeRef         = useRef(viewMode);

  // Отдельные рефы для поиска (чтобы не мешать основной пагинации)
  const searchLoadingRef    = useRef(false);
  const searchHasMoreRef    = useRef(false);
  const searchPageRef       = useRef(1);
  const searchObserverRef   = useRef(null);

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = useState(TAB_ALL);
  const [myGroups,      setMyGroups]      = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  // ── Selection state ────────────────────────────────────────────────────────
  const [selectionMode,       setSelectionMode]       = useState(false);
  const [selectedExperiments, setSelectedExperiments] = useState(new Set());
  const [showDeleteModal,     setShowDeleteModal]     = useState(false);
  const [deleting,            setDeleting]            = useState(false);

  // ── Toast state ────────────────────────────────────────────────────────────
  const [showToast,    setShowToast]    = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // Держим viewModeRef синхронизированным — loadExperiments читает его
  // через ref (по аналогии с pageRef/hasMoreRef), чтобы не пересоздавать
  // функцию при каждом переключении вида.
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // ── Derived: visible experiments ──────────────────────────────────────────
  const visibleExperiments = useMemo(() => {
    if (activeTab === TAB_ALL)  return allExperiments;
    if (activeTab === TAB_MINE) return allExperiments.filter(e => e.is_owner);
    return allExperiments.filter(e =>
      Array.isArray(e.shared_groups) &&
      e.shared_groups.some(g => g.id === activeTab)
    );
  }, [allExperiments, activeTab]);

  // ── Derived: group tabs ────────────────────────────────────────────────────
  const groupTabs = useMemo(() => {
    const seen       = new Map();
    const myGroupIds = new Set(myGroups.map(g => g.id));
    allExperiments.forEach(exp => {
      (exp.shared_groups || []).forEach(g => {
        if (!seen.has(g.id)) seen.set(g.id, g);
      });
    });
    return [...seen.values()].filter(g => myGroupIds.has(g.id));
  }, [allExperiments, myGroups]);

  // ── Функция загрузки обычных экспериментов ──────────────────────────────
  const loadExperiments = useCallback(async (pageNum, isInitial = false) => {
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      isInitial ? setInitialLoading(true) : setLoading(true);
      setError(null);

      const response = await getExperiments(pageNum, pageSizeFor(viewModeRef.current));
      if (!isMountedRef.current) return;

      const incoming = response.results ?? [];
      const more     = !!response.has_more;

      hasMoreRef.current = more;
      pageRef.current    = pageNum;

      setHasMore(more);
      setPage(pageNum);

      if (isInitial) {
        setAllExperiments(incoming);
      } else {
        setAllExperiments(prev => {
          const existingIds = new Set(prev.map(e => e.id));
          const fresh = incoming.filter(e => !existingIds.has(e.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } catch (err) {
      console.error('Error loading experiments:', err);
      if (isMountedRef.current) setError(err.message || 'Failed to load experiments');
    } finally {
      if (isMountedRef.current) {
        isInitial ? setInitialLoading(false) : setLoading(false);
        loadingRef.current = false;
      }
    }
  }, []);

  // ── Функция поиска ────────────────────────────────────────────────────────
  const performSearch = useCallback(async (query, pageNum, filterType, groupId, dateFrom, dateTo, isInitial = false) => {
    if (searchLoadingRef.current) return;

    try {
      searchLoadingRef.current = true;
      setSearchLoading(true);
      setSearchError(null);

      const params = { q: query };
      if (filterType) params.filter_type = filterType;
      if (groupId) params.group_id = groupId;
      if (pageNum) params.page = pageNum;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await searchExperiments(params);
      if (!isMountedRef.current) return;

      const incoming = response.results ?? [];
      const more     = !!response.has_more;

      // Обновляем рефы и состояние
      searchHasMoreRef.current = more;
      searchPageRef.current    = pageNum;

      setSearchHasMore(more);
      setSearchPage(pageNum);

      if (isInitial) {
        setSearchResults(incoming);
      } else {
        setSearchResults(prev => {
          const existingIds = new Set(prev.map(e => e.id));
          const fresh = incoming.filter(e => !existingIds.has(e.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } catch (err) {
      console.error('Error searching experiments:', err);
      if (isMountedRef.current) setSearchError(err.message || 'Failed to search experiments');
    } finally {
      if (isMountedRef.current) {
        setSearchLoading(false);
        searchLoadingRef.current = false;
      }
    }
  }, []);

  // ── Функция для подгрузки следующей страницы поиска ─────────────────────
  const loadMoreSearch = useCallback(() => {
    if (searchLoadingRef.current || !searchHasMoreRef.current) return;

    const nextPage = searchPageRef.current + 1;
    const filterType = activeTab === TAB_ALL ? 'all'
                     : activeTab === TAB_MINE ? 'mine'
                     : undefined;
    const groupId = (activeTab !== TAB_ALL && activeTab !== TAB_MINE) ? activeTab : undefined;

    performSearch(
      searchQuery.trim(),
      nextPage,
      filterType,
      groupId,
      searchDateFrom,
      searchDateTo,
      false
    );
  }, [searchQuery, searchDateFrom, searchDateTo, activeTab, performSearch]);

  // ── Callback для последнего элемента поискового списка ──────────────────
  const searchLastItemRef = useCallback((node) => {
    if (searchObserverRef.current) {
      searchObserverRef.current.disconnect();
      searchObserverRef.current = null;
    }
    if (!node) return;
    searchObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreSearch();
        }
      },
      { rootMargin: '200px' }
    );
    searchObserverRef.current.observe(node);
  }, [loadMoreSearch]);

  // ── Debounced поиск ────────────────────────────────────────────────────────
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (!searchQuery.trim() && !searchDateFrom && !searchDateTo) {
      setSearchResults([]);
      setSearchHasMore(false);
      setSearchPage(1);
      setShowSearchResults(false);
      return;
    }

    setShowSearchResults(true);

    debounceTimer.current = setTimeout(() => {
      const filterType = activeTab === TAB_ALL ? 'all'
                       : activeTab === TAB_MINE ? 'mine'
                       : undefined;
      const groupId = (activeTab !== TAB_ALL && activeTab !== TAB_MINE) ? activeTab : undefined;
      performSearch(
        searchQuery.trim(),
        1,
        filterType,
        groupId,
        searchDateFrom,
        searchDateTo,
        true
      );
    }, 300);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchQuery, searchDateFrom, searchDateTo, activeTab, performSearch]);

  // ── Обработчик клика вне оверлея ─────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    loadExperiments(1, true);

    const fetchGroups = async () => {
      setGroupsLoading(true);
      try {
        const data = await groupsAPI.getMyGroups();
        if (isMountedRef.current) {
          setMyGroups(Array.isArray(data) ? data : (data.results ?? []));
        }
      } catch (e) {
        console.error('Failed to load groups:', e);
      } finally {
        if (isMountedRef.current) setGroupsLoading(false);
      }
    };
    fetchGroups();

    return () => {
      isMountedRef.current = false;
      observerRef.current?.disconnect();
      searchObserverRef.current?.disconnect();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [loadExperiments]);

  // ── Обработчик смены таба ──────────────────────────────────────────────
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectionMode(false);
    setSelectedExperiments(new Set());
  };

  // ── Обработчик смены вида (grid ⇄ row) ───────────────────────────────────
  const handleViewModeChange = (mode) => {
    if (mode === viewMode) return;

    setViewMode(mode);
    // Обновляем ref сразу (не дожидаясь эффекта-синхронизации) — loadExperiments
    // ниже читает pageSize из viewModeRef синхронно в момент вызова.
    viewModeRef.current = mode;
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // localStorage недоступен — просто не сохраняем выбор вида
    }

    // page_size привязан к виду и не может "доклеиваться" посреди уже
    // начатой пагинации (сломает смысл номеров страниц на бэкенде) —
    // поэтому при смене вида полностью сбрасываем пагинацию основной ленты
    // и грузим первую страницу заново с новым page_size. Поисковый оверлей
    // не трогаем — он не зависит от viewMode.
    observerRef.current?.disconnect();
    observerRef.current = null;
    setAllExperiments([]);
    hasMoreRef.current = true;
    pageRef.current = 0;
    setHasMore(true);
    loadExperiments(1, true);
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const onClick = (experimentId) => {
    if (selectionMode) toggleExperimentSelection(experimentId);
    else navigate(`/experiment/${experimentId}`);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedExperiments(new Set());
  };

  const toggleExperimentSelection = (id) => {
    setSelectedExperiments(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedExperiments(
      selectedExperiments.size === visibleExperiments.length
        ? new Set()
        : new Set(visibleExperiments.map(e => e.id))
    );
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteExperiments = async () => {
    try {
      setDeleting(true);
      const ids = Array.from(selectedExperiments);
      await deleteExperiments(ids);

      const deleted = new Set(ids);
      setAllExperiments(prev => prev.filter(e => !deleted.has(e.id)));
      setSearchResults(prev => prev.filter(e => !deleted.has(e.id)));
      setSelectedExperiments(new Set());
      setSelectionMode(false);
      setShowDeleteModal(false);
      showToastMsg(`Deleted ${ids.length} experiment(s)`, 'success');
    } catch (err) {
      showToastMsg('Failed to delete: ' + (err.message || 'Unknown error'), 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const showToastMsg = (msg, variant = 'success') => {
    setToastMessage(msg);
    setToastVariant(variant);
    setShowToast(true);
  };

  // ── Tab counts ─────────────────────────────────────────────────────────────
  const countForTab = useCallback((tab) => {
    if (tab === TAB_ALL)  return allExperiments.length;
    if (tab === TAB_MINE) return allExperiments.filter(e => e.is_owner).length;
    return allExperiments.filter(e =>
      (e.shared_groups || []).some(g => g.id === tab)
    ).length;
  }, [allExperiments]);

  const canDeleteSelected = [...selectedExperiments].every(id => {
    const exp = allExperiments.find(e => e.id === id);
    return exp?.is_owner;
  });

  // ── Общий callback-ref для последнего элемента ленты (и grid, и row) ────
  // Наблюдатель не зависит от вида карточки — просто триггерит подгрузку
  // следующей страницы, когда последний элемент виден.
  const lastItemRef = useCallback((node) => {
    if (showSearchResults) return;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
            loadExperiments(pageRef.current + 1, false);
          }
        },
        { rootMargin: '200px' }
      );
      observerRef.current.observe(node);
    }
  }, [showSearchResults, loadExperiments]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (initialLoading) {
    return (
      <div className="ep ep--loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading experiments…</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container mt-4 ep">

      {/* ── Header ── */}
      <div className="ep__header">
        <h2>Experiments</h2>
        <div className="ep__header-actions">
          <Button variant="primary" size="sm" onClick={() => navigate('/experiment')}>
            <i className="bi bi-plus-circle me-1" />New Experiment
          </Button>

          <ButtonGroup size="sm" aria-label="View mode">
            <Button
              variant={viewMode === 'row' ? 'primary' : 'outline-secondary'}
              onClick={() => handleViewModeChange('row')}
              title="List view"
              aria-pressed={viewMode === 'row'}
            >
              <i className="bi bi-list-ul" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
              onClick={() => handleViewModeChange('grid')}
              title="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <i className="bi bi-grid-3x3-gap-fill" />
            </Button>
          </ButtonGroup>

          <Button
            variant={selectionMode ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={toggleSelectionMode}
          >
            <i className="bi bi-check-square me-1" />
            {selectionMode ? 'Cancel' : 'Select'}
          </Button>
          {selectionMode && (
            <Button
              variant="outline-secondary" size="sm"
              onClick={selectAllVisible}
              disabled={visibleExperiments.length === 0}
            >
              {selectedExperiments.size === visibleExperiments.length
                ? 'Deselect All' : 'Select All'}
            </Button>
          )}
          {selectionMode && selectedExperiments.size > 0 && (
            <Badge bg="primary">{selectedExperiments.size} selected</Badge>
          )}
        </div>
      </div>

      {/* ── Bulk action bar ── */}
      {selectionMode && selectedExperiments.size > 0 && (
        <div className="ep__bulk-bar">
          {canDeleteSelected ? (
            <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)}>
              <i className="bi bi-trash me-1" />Delete ({selectedExperiments.size})
            </Button>
          ) : (
            <span className="ep__bulk-hint">
              <i className="bi bi-info-circle me-1" />
              You can only delete experiments you own.
            </span>
          )}
          <Button variant="outline-primary" size="sm"
                  onClick={() => showToastMsg('Analysis started', 'info')}>
            <i className="bi bi-bar-chart me-1" />Analyze
          </Button>
          <Button variant="outline-secondary" size="sm"
                  onClick={() => showToastMsg('Export initiated', 'info')}>
            <i className="bi bi-download me-1" />Export
          </Button>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {/* ── Поисковая строка с фильтрами по дате ── */}
      <div className="ep__search-wrapper" ref={searchContainerRef}>
        <div className="ep__search-row">
          <InputGroup className="ep__search-input">
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search experiments by name, user email, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => {
                if (searchQuery.trim() || searchDateFrom || searchDateTo) {
                  setShowSearchResults(true);
                }
              }}
            />
            {searchQuery && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <i className="bi bi-x-lg" />
              </Button>
            )}
          </InputGroup>

          <div className="ep__search-dates">
            <Form.Control
              type="date"
              value={searchDateFrom}
              onChange={(e) => setSearchDateFrom(e.target.value)}
              placeholder="From"
              size="sm"
            />
            <span className="ep__search-dates-sep">—</span>
            <Form.Control
              type="date"
              value={searchDateTo}
              onChange={(e) => setSearchDateTo(e.target.value)}
              placeholder="To"
              size="sm"
            />
          </div>
        </div>

        {/* ── Оверлей с результатами поиска ── */}
        {showSearchResults && (
          <div className="ep__search-overlay">
            {searchLoading && searchResults.length === 0 && (
              <div className="ep__search-loading">
                <Spinner animation="border" size="sm" />
                <span>Searching…</span>
              </div>
            )}
            {searchError && (
              <div className="ep__search-error">{searchError}</div>
            )}
            {!searchLoading && !searchError && searchResults.length === 0 && (
              <div className="ep__search-empty">
                <i className="bi bi-search" />
                <span>No experiments found</span>
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="ep__search-list">
                {searchResults.map((exp, index) => {
                  const isLast = index === searchResults.length - 1;
                  return (
                    <div
                      key={exp.id}
                      className="ep__search-item"
                      ref={isLast ? searchLastItemRef : null}
                      onClick={() => {
                        navigate(`/experiment/${exp.id}`);
                        setShowSearchResults(false);
                      }}
                    >
                      <div className="ep__search-item-name">{exp.name}</div>
                      <div className="ep__search-item-meta">
                        <span>{exp.user_email}</span>
                        <span className="ep__search-item-date">
                          {new Date(exp.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {searchLoading && searchResults.length > 0 && (
                  <div className="ep__search-load-more">
                    <Spinner animation="border" size="sm" />
                    <span>Loading more…</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div className="ep__tabs" role="tablist">
        <button
          role="tab" aria-selected={activeTab === TAB_ALL}
          className={`ep__tab ${activeTab === TAB_ALL ? 'ep__tab--active' : ''}`}
          onClick={() => handleTabChange(TAB_ALL)}
        >
          <i className="bi bi-grid me-1" />All
          <span className="ep__tab-count">{countForTab(TAB_ALL)}</span>
        </button>

        <button
          role="tab" aria-selected={activeTab === TAB_MINE}
          className={`ep__tab ${activeTab === TAB_MINE ? 'ep__tab--active' : ''}`}
          onClick={() => handleTabChange(TAB_MINE)}
        >
          <i className="bi bi-person me-1" />Mine
          <span className="ep__tab-count">{countForTab(TAB_MINE)}</span>
        </button>

        {groupTabs.length > 0 && <div className="ep__tab-divider" aria-hidden="true" />}

        {groupsLoading && (
          <span className="ep__tab-loading">
            <Spinner animation="border" size="sm" />
          </span>
        )}

        {!groupsLoading && groupTabs.map(group => (
          <button
            key={group.id} role="tab"
            aria-selected={activeTab === group.id}
            className={`ep__tab ep__tab--group ${activeTab === group.id ? 'ep__tab--active' : ''}`}
            onClick={() => handleTabChange(group.id)}
            title={group.name}
          >
            <i className="bi bi-people me-1" />
            <span className="ep__tab-label">{group.name}</span>
            <span className="ep__tab-count">{countForTab(group.id)}</span>
          </button>
        ))}
      </div>

      {/* ── Group context banner ── */}
      <ActiveTabBanner activeTab={activeTab} groupTabs={groupTabs} count={visibleExperiments.length} />

      {/* ── Feed (grid или row — в зависимости от viewMode) ── */}
      {visibleExperiments.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="ep__grid">
            {visibleExperiments.map((experiment, index) => {
              const isLast = index === visibleExperiments.length - 1;
              return (
                <div
                  key={experiment.id}
                  className="ep__grid-item"
                  ref={isLast ? lastItemRef : null}
                >
                  <ExperimentCard
                    experiment={experiment}
                    onClick={() => onClick(experiment.id)}
                    selectionMode={selectionMode}
                    isSelected={selectedExperiments.has(experiment.id)}
                    onToggleSelect={() => toggleExperimentSelection(experiment.id)}
                    showOwnerBadge={activeTab !== TAB_MINE}
                    showGroupBadges={activeTab === TAB_ALL || activeTab === TAB_MINE}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ep__list">
            {visibleExperiments.map((experiment, index) => {
              const isLast = index === visibleExperiments.length - 1;
              return (
                <div
                  key={experiment.id}
                  className="ep__list-item"
                  ref={isLast ? lastItemRef : null}
                >
                  <ExperimentRow
                    experiment={experiment}
                    onClick={() => onClick(experiment.id)}
                    selectionMode={selectionMode}
                    isSelected={selectedExperiments.has(experiment.id)}
                    onToggleSelect={() => toggleExperimentSelection(experiment.id)}
                    showOwnerBadge={activeTab !== TAB_MINE}
                    showGroupBadges={activeTab === TAB_ALL || activeTab === TAB_MINE}
                  />
                </div>
              );
            })}
          </div>
        )
      ) : (
        <EmptyState
          activeTab={activeTab}
          groupTabs={groupTabs}
          onCreateNew={() => navigate('/experiment')}
        />
      )}

      {/* ── Loading spinner for pagination ── */}
      {loading && (
        <div className="ep__load-more">
          <Spinner animation="border" size="sm" />
          <span>Loading more…</span>
        </div>
      )}

      {!hasMore && visibleExperiments.length > 0 && !loading && (
        <p className="ep__end-msg">All experiments loaded</p>
      )}

      {/* ── Delete modal ── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }} />
          <h5 className="mt-3">Are you sure?</h5>
          <p className="text-muted mb-0">
            You are about to permanently delete{' '}
            <strong>{selectedExperiments.size}</strong> experiment(s).
            This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteExperiments} disabled={deleting}>
            {deleting
              ? <><Spinner animation="border" size="sm" className="me-2" />Deleting…</>
              : <><i className="bi bi-trash me-1" />Delete {selectedExperiments.size}</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Toast ── */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)}
               delay={4000} autohide bg={toastVariant}>
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Success'
               : toastVariant === 'danger' ? 'Error' : 'Info'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'danger' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const ActiveTabBanner = ({ activeTab, groupTabs, count }) => {
  if (activeTab === TAB_ALL || activeTab === TAB_MINE) return null;
  const group = groupTabs.find(g => g.id === activeTab);
  if (!group) return null;
  return (
    <div className="ep__tab-banner">
      <i className="bi bi-people-fill me-2" />
      Showing <strong>{count}</strong> experiment{count !== 1 ? 's' : ''} shared
      with <strong>{group.name}</strong>
    </div>
  );
};

const EmptyState = ({ activeTab, groupTabs, onCreateNew }) => {
  if (activeTab === TAB_ALL) return (
    <div className="ep__empty">
      <i className="bi bi-beaker ep__empty-icon" />
      <h5>No experiments yet</h5>
      <p>Create your first experiment to get started</p>
      <Button variant="primary" onClick={onCreateNew}>Create Experiment</Button>
    </div>
  );
  if (activeTab === TAB_MINE) return (
    <div className="ep__empty">
      <i className="bi bi-person-x ep__empty-icon" />
      <h5>No experiments owned by you</h5>
      <p>Experiments you create will appear here</p>
      <Button variant="primary" onClick={onCreateNew}>Create Experiment</Button>
    </div>
  );
  const group = groupTabs.find(g => g.id === activeTab);
  return (
    <div className="ep__empty">
      <i className="bi bi-share ep__empty-icon" />
      <h5>No experiments shared with {group?.name ?? 'this group'}</h5>
      <p>Share experiments from their detail page to make them visible here</p>
    </div>
  );
};

export default ExperimentsPage;
